/**
 * I messaggi dei campi personalizzati (CF13) — CL08.
 *
 * Due cose, e nessuna delle due e' un secondo catalogo:
 *
 * - **`CUSTOM_FIELDS_MESSAGE_KEYS`** e' l'elenco delle famiglie che il catalogo
 *   server-side deve contenere, con le chiavi riusate accanto. Non e' una copia
 *   del catalogo: e' la lista di cosa chiedere al deploy. Un catalogo locale
 *   sarebbe una seconda verita' che diverge da quella che l'utente vede.
 * - **`formatMessage`** sostituisce i token di un template con i parametri. Il
 *   catalogo restituisce `"Il codice {code} e' gia' utilizzato"` e i parametri
 *   arrivano dal server: comporre la frase e' l'unica cosa che deve fare il
 *   client.
 *
 * ## Le due trappole che questo file evita
 *
 * **`$&` non e' testo.** In `String.prototype.replace` la stringa di
 * sostituzione interpreta `$&`, `` $` ``, `$'` e `$1`: passare i parametri come
 * stringa farebbe sparire `$&` dal messaggio, o inserirebbe il testo che lo
 * circonda. La sostituzione usa una **funzione**, che quei pattern non li
 * interpreta — ed e' il motivo per cui il test del piano passa `'$&<img>'`
 * come parametro.
 *
 * **Nessun HTML.** Il valore sostituito finisce nel DOM come **testo**, e React
 * fa l'escaping al render. Non c'e' `dangerouslySetInnerHTML` da nessuna parte,
 * e non deve essercene: un messaggio che contenesse markup verrebbe mostrato
 * come markup, cioe' eseguito.
 *
 * **Un parametro mancante e' un errore, non una stringa vuota.** Un messaggio
 * che dicesse "Il codice  e' gia' utilizzato" nasconderebbe il difetto; il
 * chiamante deve saperlo, perche' la cosa giusta e' **non mostrare** la
 * conferma — non mostrarla muta.
 */

// Nessun import da `common`: `utilities.ts` importa `unified`, che è ESM, e la
// suite `jest.pure` non riuscirebbe più a caricare questo modulo. La traduzione
// arriva già risolta dal chiamante.

/**
 * Le famiglie che il catalogo deve contenere, e le chiavi esistenti riusate.
 *
 * Le prime sono proposte dalla spec (`generic-specs.md` §10); le seconde
 * esistono gia' nel catalogo e sono verificate — riusarle evita di aggiungere
 * chiavi dove ce n'e' gia' una.
 */
export const CUSTOM_FIELDS_MESSAGE_KEYS: readonly string[] = [
  // Famiglie proposte dalla spec.
  "custom_fields.type.",
  "custom_fields.group.",
  "custom_fields.config.",
  "custom_fields.visibility.",
  "custom_fields.required.",
  "custom_fields.reconciliation.",
  "custom_fields.sequence.",
  "custom_fields.impact.",
  "custom_fields.formula.",
  "custom_fields.error.",
  "custom_fields.action.",
  "custom_fields.operation.status.",
  // Import/export del file `.flw`: le frasi sul contenuto del file, piu' una
  // chiave esatta per l'esportazione che non riesce a leggere lo schema.
  "custom_fields.import.",
  "custom_fields.export.schema_unavailable",
  // Chiavi esistenti, riusate invece che duplicate.
  "process_model.schema.title",
  "process_model.schema.description",
  "process_model.schema.field.name",
  "process_model.schema.field.type",
  "process_model.schema.add_field",
  "generic.showingincard.caption",
  "pm.button.save",
  "register.fieldrequired",
];

/** Un token di sostituzione: `{nome}`, e nient'altro. */
const TOKEN = /\{([a-zA-Z][a-zA-Z0-9_]*)\}/g;

/**
 * Compone un messaggio dal template e dai parametri.
 *
 * Lancia se un parametro manca — vedi il commento in testa: un messaggio muto
 * nasconderebbe il difetto, e il chiamante deve poter decidere di **non**
 * mostrare la conferma.
 *
 * La sostituzione e' una funzione e non una stringa: `$&` in un parametro e'
 * testo, e deve restare tale.
 */
export function formatMessage(
  template: string,
  params: Record<string, string | number>
): string {
  return template.replace(TOKEN, (_, key: string) => {
    if (!Object.prototype.hasOwnProperty.call(params, key)) {
      throw new Error(`Missing message parameter: ${key}`);
    }
    return String(params[key]);
  });
}

/**
 * Compone un messaggio, o restituisce `null` se non si puo'.
 *
 * Il fallback per chi renderizza: un errore di composizione non deve far
 * esplodere un componente, e **non** deve produrre un messaggio a meta'. Chi
 * chiama decide cosa mostrare al posto suo — e per un'azione sensibile, la
 * risposta giusta e' disabilitarla.
 */
export function tryFormatMessage(
  template: string,
  params: Record<string, string | number>
): string | null {
  try {
    return formatMessage(template, params);
  } catch {
    return null;
  }
}

/**
 * Il messaggio tradotto, con i suoi parametri già sostituiti.
 *
 * Serve dove il catalogo porta un segnaposto — `custom_fields.visibility.title`
 * è `"Visibilità di {field}"`: rendendo la chiave con `getTranslation` quel
 * `{field}` finisce a schermo alla lettera, ed è quello che succedeva.
 *
 * Prende il **template già risolto**, non le traduzioni: risolvere la chiave è
 * `getTranslation`, che vive in `common/utilities`, e questo modulo deve
 * restare puro (vedi in testa al file).
 *
 * Se la composizione non riesce si rende il template com'è: il segnaposto resta
 * visibile, e un difetto visibile è meglio di un titolo muto.
 */
export function translateMessage(
  template: string,
  params?: Record<string, string | number>
): string {
  return params ? tryFormatMessage(template, params) ?? template : template;
}
